import { useEffect, useRef } from 'react';
import { useTour } from '../contexts/TourContext';
import {
  MMEC_TOUR_AUTO_START_KEY,
  MMEC_TOUR_SESSION_DONE_KEY,
} from '../tour/mmecPlatformTour';

/** Auto-start platform tour once per session after legal disclaimer accept. */
const CompanionTourLauncher: React.FC = () => {
  const { startTour, isTourActive } = useTour();
  const startedRef = useRef(false);

  useEffect(() => {
    const tryStart = () => {
      if (startedRef.current || isTourActive) return;
      try {
        if (sessionStorage.getItem(MMEC_TOUR_SESSION_DONE_KEY) === '1') return;
        if (sessionStorage.getItem(MMEC_TOUR_AUTO_START_KEY) !== '1') return;
      } catch {
        return;
      }
      startedRef.current = true;
      window.setTimeout(() => startTour(), 600);
    };

    // The tour is opt-in: it never arms itself on load or on legal accept, so
    // tryStart() here is a no-op unless something explicitly armed the tour.
    // A deliberate request starts it on demand (the "Start tour" controls also
    // call startTour() directly).
    const requestStart = () => {
      if (startedRef.current || isTourActive) return;
      startedRef.current = true;
      window.setTimeout(() => startTour(), 100);
    };
    tryStart();
    window.addEventListener('mmec-request-platform-tour', requestStart);
    return () => {
      window.removeEventListener('mmec-request-platform-tour', requestStart);
    };
  }, [startTour, isTourActive]);

  useEffect(() => {
    if (!isTourActive) return;
    const onEnd = () => {
      try {
        sessionStorage.setItem(MMEC_TOUR_SESSION_DONE_KEY, '1');
        sessionStorage.removeItem(MMEC_TOUR_AUTO_START_KEY);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('mmec-platform-tour-ended', onEnd);
    return () => window.removeEventListener('mmec-platform-tour-ended', onEnd);
  }, [isTourActive]);

  return null;
};

export default CompanionTourLauncher;
